import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Check, LoaderCircle, RefreshCw, Save, Trash2 } from "lucide-react";
import type { InvoiceLayout, InvoiceLayoutTemplate } from "@shared/invoice-layout";
import { InvoiceApi } from "@/lib/invoice-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type InvoiceLayoutTemplatesProps = {
  layout: InvoiceLayout | undefined;
  onChange: (layout: InvoiceLayout) => void;
};

function copyLayout(layout: InvoiceLayout | undefined): InvoiceLayout {
  return Object.fromEntries(
    Object.entries(layout ?? {}).map(([id, offset]) => [
      id,
      offset ? { x: offset.x, y: offset.y } : offset,
    ]),
  ) as InvoiceLayout;
}

export default function InvoiceLayoutTemplates({ layout, onChange }: InvoiceLayoutTemplatesProps) {
  const [templates, setTemplates] = useState<InvoiceLayoutTemplate[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [newName, setNewName] = useState("");
  const [renameName, setRenameName] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const selectedTemplate = templates.find((template) => template.id === selectedId);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const result = await InvoiceApi.layoutTemplates();
      setTemplates(result);
      setSelectedId((current) => result.some((template) => template.id === current) ? current : "");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Vorlagen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    setRenameName(selectedTemplate?.name ?? "");
  }, [selectedTemplate?.id, selectedTemplate?.name]);

  const createTemplate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const created = await InvoiceApi.createLayoutTemplate(name, copyLayout(layout));
      setTemplates((current) => [...current, created]);
      setSelectedId(created.id);
      setNewName("");
      setMessage(`Vorlage „${created.name}“ gespeichert. Die Rechnung wurde nicht geändert.`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Vorlage konnte nicht gespeichert werden.");
    } finally {
      setBusy(false);
    }
  };

  const renameTemplate = async () => {
    if (!selectedTemplate || !renameName.trim()) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const updated = await InvoiceApi.renameLayoutTemplate(selectedTemplate.id, renameName.trim());
      setTemplates((current) => current.map((template) => template.id === updated.id ? updated : template));
      setMessage("Vorlagenname geändert.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Vorlage konnte nicht umbenannt werden.");
    } finally {
      setBusy(false);
    }
  };

  const deleteTemplate = async () => {
    if (!selectedTemplate) return;
    const confirmed = window.confirm(
      `Vorlage „${selectedTemplate.name}“ löschen? Bereits gespeicherte Rechnungen bleiben unverändert.`,
    );
    if (!confirmed) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await InvoiceApi.deleteLayoutTemplate(selectedTemplate.id);
      setTemplates((current) => current.filter((template) => template.id !== selectedTemplate.id));
      setSelectedId("");
      setMessage("Vorlage gelöscht. Bereits gespeicherte Rechnungen bleiben unverändert.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Vorlage konnte nicht gelöscht werden.");
    } finally {
      setBusy(false);
    }
  };

  const applyTemplate = () => {
    if (!selectedTemplate) return;
    onChange(copyLayout(selectedTemplate.layout));
    setMessage("Layout übernommen. Speichern Sie anschließend die Rechnung, um die Änderung zu sichern.");
    setError("");
  };

  return (
    <section className="space-y-3 rounded-md border border-slate-200 bg-slate-50/60 p-4" aria-labelledby="invoice-layout-templates-title">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 id="invoice-layout-templates-title" className="text-sm font-semibold text-slate-800">
            Eigene Deckblattlayouts
          </h3>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-slate-600">
            Vorlagen speichern ausschließlich die Positionen der Bereiche. Logos, Inhalte und Beträge bleiben unberührt.
            Eine Vorlage wird erst durch „Layout übernehmen“ auf diese Rechnung angewendet.
          </p>
        </div>
        {loading && (
          <span className="inline-flex items-center gap-2 text-xs text-slate-500" role="status">
            <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Vorlagen werden geladen
          </span>
        )}
      </div>

      {error && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
          <span>{error}</span>
          <Button type="button" variant="outline" size="sm" onClick={() => void loadTemplates()} disabled={loading || busy}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
            Erneut versuchen
          </Button>
        </div>
      )}

      {!error && !loading && templates.length === 0 && (
        <p className="rounded-md border border-dashed border-slate-300 bg-white/70 px-3 py-2 text-sm text-slate-600">
          Noch keine gespeicherten Vorlagen. Speichern Sie das aktuelle Layout, um es später wiederzuverwenden.
        </p>
      )}

      <div className="grid gap-3 md:grid-cols-[minmax(12rem,1fr)_auto] md:items-end">
        <div className="space-y-1.5">
          <label htmlFor="invoice-layout-template" className="text-sm font-medium text-slate-700">
            Vorlage auswählen
          </label>
          <select
            id="invoice-layout-template"
            value={selectedId}
            onChange={(event) => {
              setSelectedId(event.target.value);
              setMessage("");
            }}
            disabled={loading || busy || templates.length === 0}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bauwerk-teal disabled:cursor-not-allowed disabled:opacity-60"
          >
            <option value="">Keine Vorlage ausgewählt</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>{template.name}</option>
            ))}
          </select>
        </div>
        <Button type="button" onClick={applyTemplate} disabled={loading || busy || !selectedTemplate}>
          Layout übernehmen
        </Button>
      </div>

      {selectedTemplate && (
        <div className="grid gap-3 border-t border-slate-200 pt-3 sm:grid-cols-[minmax(12rem,1fr)_auto_auto] sm:items-end">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void renameTemplate();
            }}
            className="space-y-1.5"
          >
            <label htmlFor="invoice-layout-template-rename" className="text-sm font-medium text-slate-700">
              Ausgewählte Vorlage umbenennen
            </label>
            <Input
              id="invoice-layout-template-rename"
              value={renameName}
              maxLength={100}
              onChange={(event) => setRenameName(event.target.value)}
              disabled={busy}
              required
              aria-label="Neuer Vorlagenname"
            />
          </form>
          <Button type="button" variant="outline" onClick={() => void renameTemplate()} disabled={busy || !renameName.trim() || renameName.trim() === selectedTemplate.name}>
            <Check className="mr-2 h-4 w-4" aria-hidden="true" />
            Umbenennen
          </Button>
          <Button type="button" variant="outline" onClick={() => void deleteTemplate()} disabled={busy} className="text-red-700 hover:text-red-800">
            <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
            Löschen
          </Button>
        </div>
      )}

      <form onSubmit={createTemplate} className="grid gap-2 border-t border-slate-200 pt-3 sm:grid-cols-[minmax(12rem,1fr)_auto] sm:items-end">
        <div className="space-y-1.5">
          <label htmlFor="invoice-layout-template-new" className="text-sm font-medium text-slate-700">
            Aktuelles Layout als neue Vorlage speichern
          </label>
          <Input
            id="invoice-layout-template-new"
            value={newName}
            maxLength={100}
            placeholder="Vorlagenname"
            onChange={(event) => setNewName(event.target.value)}
            disabled={busy}
            required
          />
        </div>
         <Button type="submit" variant="outline" disabled={loading || busy || !newName.trim()}>
          <Save className="mr-2 h-4 w-4" aria-hidden="true" />
          Als neue Vorlage speichern
        </Button>
      </form>

      {busy && <p className="text-xs text-slate-500" role="status">Änderung wird gespeichert …</p>}
      {message && <p className="text-sm text-emerald-800" role="status">{message}</p>}
    </section>
  );
}
